import type { AuftragMini } from "@/lib/orders";
import { formatAuftragsnummerDisplay } from "@/lib/orderDraftUtils";
import type { AnfrageDraftIn, AnfrageMailReviewItem, AnfragePosition, AnfrageStatus } from "@/lib/anfragen";
import { plainBodyToSignedMailHtml } from "@/lib/mailSignature";

export const LOCAL_DRAFT_KEY = "eh.anfrage.draft.v1";
export const LOCAL_DRAFT_ID_KEY = "eh.anfrage.draftId";

export type RecipientMode = "hero" | "manual";

export interface TaRecipientContact {
  email?: string;
  first_name?: string;
  last_name?: string;
  anrede?: string;
  title?: string;
  is_company_email?: boolean;
}

export interface TaRecipient {
  id: number;
  mode: RecipientMode;
  supplier: {
    id: string;
    name: string;
    email?: string;
    geschaeftspartner_id?: number | null;
    has_logo?: boolean | null;
    logo_updated_at?: string | null;
  } | null;
  contact: TaRecipientContact | null;
  manual: {
    firm: string;
    anrede: string;
    name: string;
    email: string;
  };
}

export interface DraftPosition extends AnfragePosition {
  localId: string;
}

export interface AnfrageWizardState {
  recipients: TaRecipient[];
  kuerzel: string;
  auftraege: AuftragMini[];
  positionen: DraftPosition[];
  taPreview: Record<string, string>;
  draftId: number | null;
  snippetTemplateId: number | null;
  mailSubject: string;
  mailBody: string;
  /** HTML-Vorschau inkl. Signatur, analog zur Bestell-Mail. */
  mailBodyHtml: string;
  placeholders: number;
  /** Quell-MA für Split-Ansicht neben dem Wizard. */
  sourceMaId: number | null;
}

let nextRecipientId = 1;
let nextPosId = 1;

export function newRecipientId(): number {
  return nextRecipientId++;
}

export function newPositionLocalId(): string {
  return `pos_${nextPosId++}`;
}

export function createRecipient(initial?: Partial<TaRecipient>): TaRecipient {
  return {
    id: newRecipientId(),
    mode: initial?.mode === "manual" ? "manual" : "hero",
    supplier: initial?.supplier || null,
    contact: initial?.contact || null,
    manual: {
      firm: "",
      anrede: "",
      name: "",
      email: "",
      ...(initial?.manual || {}),
    },
  };
}

export function emptyWizardState(kuerzel = ""): AnfrageWizardState {
  return {
    recipients: [createRecipient()],
    kuerzel,
    auftraege: [],
    positionen: [],
    taPreview: {},
    draftId: null,
    snippetTemplateId: null,
    mailSubject: "",
    mailBody: "",
    mailBodyHtml: "",
    placeholders: 1,
    sourceMaId: null,
  };
}

export function anfrageSendLabel(recipientCount: number): string {
  return recipientCount > 1 ? "Anfragen versenden" : "Anfrage versenden";
}

export function labelAnfrageStatus(status: AnfrageStatus | string | null | undefined): string {
  if (status === "draft") return "Entwurf";
  if (status === "ready") return "Noch nicht versendet";
  if (status === "sent") return "Versendet";
  if (status === "erledigt") return "Erledigt";
  return status || "–";
}

export function canConvertAnfrage(row: { status?: AnfrageStatus | string | null } | null): boolean {
  const s = row?.status;
  return s === "ready" || s === "sent" || s === "erledigt";
}

export function canCloseAnfrage(row: { status?: AnfrageStatus | string | null } | null): boolean {
  const s = row?.status;
  return s === "ready" || s === "sent";
}

export function statusBadgeClass(status: AnfrageStatus | string | null | undefined): string {
  if (status === "sent") return "bg-accent-soft text-accent";
  if (status === "ready") return "bg-status-orange/15 text-status-orange";
  if (status === "draft") return "bg-fill-tertiary text-label-secondary";
  if (status === "erledigt") return "bg-status-green/15 text-status-green";
  return "bg-fill-tertiary text-label-secondary";
}

/**
 * Ablaufstand einer Anfrage – aus Status, Antworten und Bestell-Verknüpfungen
 * abgeleitet. Steuert Badge, Gruppierung und den „Nächster Schritt“-Button.
 */
export type AnfrageStage = "draft" | "ready" | "waiting" | "answered" | "ordered" | "done";

export interface AnfrageStageSource {
  status?: AnfrageStatus | string | null;
  tk_links?: unknown[] | null;
  pending_mail_count?: number | null;
  answer_count?: number | null;
}

export function anfrageStage(row: AnfrageStageSource, pendingOverride?: number): AnfrageStage {
  if (row.status === "draft") return "draft";
  if (row.status === "erledigt") return "done";
  if ((row.tk_links || []).length > 0) return "ordered";
  if (row.status === "ready") return "ready";
  const pending = pendingOverride ?? Number(row.pending_mail_count || 0);
  const answers = Number(row.answer_count || 0);
  if (pending > 0 || answers > 0) return "answered";
  return "waiting";
}

export const ANFRAGE_STAGE_ORDER: AnfrageStage[] = [
  "answered",
  "ready",
  "draft",
  "waiting",
  "ordered",
  "done",
];

export function stageLabel(stage: AnfrageStage): string {
  switch (stage) {
    case "draft":
      return "Entwurf";
    case "ready":
      return "Noch nicht versendet";
    case "waiting":
      return "Wartet auf Antwort";
    case "answered":
      return "Antwort erhalten";
    case "ordered":
      return "Bestellt";
    case "done":
      return "Erledigt";
  }
}

export function stageBadgeClass(stage: AnfrageStage): string {
  switch (stage) {
    case "draft":
      return "bg-fill-tertiary text-label-secondary";
    case "ready":
      return "bg-status-orange/15 text-status-orange";
    case "waiting":
      return "bg-accent-soft text-accent";
    case "answered":
      return "bg-status-green/15 text-status-green";
    case "ordered":
      return "bg-status-indigo/15 text-status-indigo";
    case "done":
      return "bg-fill-tertiary text-label-secondary";
  }
}

/** Farbstreifen links an der Listenkarte. */
export function stageAccentClass(stage: AnfrageStage): string {
  switch (stage) {
    case "draft":
      return "bg-label-quaternary";
    case "ready":
      return "bg-status-orange";
    case "waiting":
      return "bg-accent";
    case "answered":
      return "bg-status-green";
    case "ordered":
      return "bg-status-indigo";
    case "done":
      return "bg-separator-opaque";
  }
}

/** Beschriftung des primären Buttons je Ablaufstand. */
export function stagePrimaryAction(stage: AnfrageStage): string {
  switch (stage) {
    case "draft":
      return "Weiter bearbeiten";
    case "ready":
      return "Jetzt versenden";
    case "answered":
      return "Antwort ansehen";
    case "waiting":
    case "ordered":
    case "done":
      return "Öffnen";
  }
}

/** „vor 3 Tagen“, „heute“, „gestern“ – für Wartezeiten in der Liste. */
export function fmtRelativeDays(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfThat = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfToday - startOfThat) / 86_400_000);
  if (days <= 0) return "heute";
  if (days === 1) return "gestern";
  if (days < 7) return `vor ${days} Tagen`;
  if (days < 14) return "vor 1 Woche";
  if (days < 60) return `vor ${Math.round(days / 7)} Wochen`;
  return `vor ${Math.round(days / 30)} Monaten`;
}

export function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtMailDate(iso: string | null | undefined): string {
  return fmtDateTime(iso);
}

export function matchReasonLabel(reason: string): string {
  switch (reason) {
    case "conversation":
      return "Antwort im Thread";
    case "in_reply_to":
      return "Header-Match";
    case "ta_in_subject":
      return "TA im Betreff";
    case "ta_in_body":
      return "TA im Text";
    case "ta_in_attachment":
      return "TA im Anhang";
    case "sender_match":
      return "Absender = Lieferant";
    default:
      return reason;
  }
}

/** `{{Signatur}}` im Vorlagentext entfernen; die Signatur wird separat angehängt. */
export function stripSignaturePlaceholder(text: string): string {
  return String(text || "")
    .replace(/\{\{\s*Signatur\s*\}\}/gi, "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function plainFromMailNode(root: ParentNode): string {
  const el = document.createElement("div");
  el.innerHTML = root instanceof HTMLElement ? root.innerHTML : "";
  el.querySelectorAll("style,script").forEach((node) => node.remove());
  el.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
  el.querySelectorAll("p,div,tr,li,h1,h2,h3,h4,blockquote").forEach((block) => {
    block.append("\n");
  });
  return (el.textContent || "")
    .replace(/\u00a0/g, " ")
    .split("\n")
    .map((line) => line.trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Nachricht ohne Signaturblock, plus Klartext inkl. Signatur fürs Mailprogramm. */
export function mailHtmlToPlainParts(html: string): { messagePlain: string; fullPlain: string } {
  if (!html?.trim() || typeof document === "undefined") {
    return { messagePlain: "", fullPlain: "" };
  }
  const full = document.createElement("div");
  full.innerHTML = html;
  const fullPlain = plainFromMailNode(full);
  full.querySelectorAll(".eh-mail-signature").forEach((node) => node.remove());
  return { messagePlain: plainFromMailNode(full), fullPlain };
}

export async function buildAnfrageMailReviewItems(
  items: Array<{
    anfrage_id: number;
    to: string;
    supplier_label?: string;
    subject: string;
    body: string;
  }>,
): Promise<AnfrageMailReviewItem[]> {
  return Promise.all(
    items.map(async (it) => ({
      anfrage_id: it.anfrage_id,
      to: it.to,
      supplier_label: it.supplier_label,
      subject: it.subject,
      bodyHtml: await plainBodyToSignedMailHtml(stripSignaturePlaceholder(it.body)),
    })),
  );
}

export function buildMailtoHref(to: string, subject: string, body: string): string {
  const bodyCRLF = String(body || "").replace(/\r?\n/g, "\r\n");
  const qp = [
    ["subject", subject],
    ["body", bodyCRLF],
  ]
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v ?? ""))}`)
    .join("&");
  return `mailto:${encodeURIComponent(to)}?${qp}`;
}

function recipientRowToDraftFields(r: TaRecipient) {
  if (r.mode === "hero") {
    const c = r.contact || {};
    const email = (c.email || "").trim();
    return {
      supplier_id: r.supplier?.id != null ? String(r.supplier.id) : "",
      supplier_name: r.supplier?.name || "",
      supplier_email: email === "" ? null : email,
      supplier_contact_anrede: c.anrede || "",
      supplier_contact_first_name: c.first_name || "",
      supplier_contact_last_name: c.last_name || "",
      supplier_contact_name: [c.first_name, c.last_name].filter(Boolean).join(" ").trim(),
      template_id: null,
    };
  }
  const anrede = (r.manual.anrede || "").trim();
  const name = (r.manual.name || "").trim();
  const email = (r.manual.email || "").trim();
  return {
    supplier_id: "",
    supplier_name: (r.manual.firm || "").trim() || null,
    supplier_email: email === "" ? null : email,
    supplier_contact_anrede: anrede === "" ? null : anrede,
    supplier_contact_name: name === "" ? null : name,
    supplier_contact_first_name: null,
    supplier_contact_last_name: null,
    template_id: null,
  };
}

export function validateTaRecipients(recipients: TaRecipient[]): string | null {
  for (let i = 0; i < recipients.length; i++) {
    const r = recipients[i];
    if (!r) continue;
    const label = `Lieferant ${i + 1}`;
    if (r.mode === "hero") {
      if (!r.supplier?.id) return `${label}: Bitte einen Lieferanten aus Hero wählen.`;
      if (!(r.contact?.email || "").trim()) return `${label}: Bitte einen Ansprechpartner mit E-Mail wählen.`;
    } else {
      if (!(r.manual.firm || "").trim()) return `${label}: Bitte den Firmennamen angeben.`;
      if (!(r.manual.email || "").trim()) return `${label}: Bitte eine E-Mail angeben.`;
    }
  }
  return null;
}

export function stateToDraftIn(state: AnfrageWizardState): AnfrageDraftIn {
  const rows = state.recipients.map(recipientRowToDraftFields);
  const primary = rows[0] || {
    supplier_id: "",
    supplier_name: "",
    supplier_email: null,
    supplier_contact_anrede: "",
    supplier_contact_first_name: "",
    supplier_contact_last_name: "",
    supplier_contact_name: "",
    template_id: null,
  };
  const zusatz = rows.slice(1).map((row) => ({
    supplier_id: row.supplier_id || null,
    supplier_name: row.supplier_name,
    supplier_email: row.supplier_email,
    supplier_contact_first_name: row.supplier_contact_first_name,
    supplier_contact_last_name: row.supplier_contact_last_name,
    supplier_contact_anrede: row.supplier_contact_anrede,
    supplier_contact_name: row.supplier_contact_name,
    template_id: null,
  }));

  return {
    supplier_id: primary.supplier_id ?? "",
    supplier_name: primary.supplier_name,
    supplier_contact_first_name: primary.supplier_contact_first_name,
    supplier_contact_last_name: primary.supplier_contact_last_name,
    supplier_contact_anrede: primary.supplier_contact_anrede,
    supplier_contact_name: primary.supplier_contact_name,
    supplier_email: primary.supplier_email,
    template_id: null,
    snippet_template_id:
      state.snippetTemplateId != null && Number.isFinite(Number(state.snippetTemplateId))
        ? Number(state.snippetTemplateId)
        : null,
    kuerzel: state.kuerzel || "",
    auftraege: state.auftraege,
    positionen: state.positionen.map(({ localId: _localId, ...p }) => p),
    notizen: "",
    zusatz_empfaenger: zusatz,
    source_ma_id: state.sourceMaId ?? null,
  };
}

export function persistLocalDraft(state: AnfrageWizardState): void {
  try {
    localStorage.setItem(
      LOCAL_DRAFT_KEY,
      JSON.stringify({
        recipients: state.recipients,
        auftraege: state.auftraege,
        positionen: state.positionen,
        kuerzel: state.kuerzel,
        snippetTemplateId: state.snippetTemplateId,
      }),
    );
    if (state.draftId) localStorage.setItem(LOCAL_DRAFT_ID_KEY, String(state.draftId));
  } catch {
    /* ignore */
  }
}

export function clearLocalDraft(): void {
  try {
    localStorage.removeItem(LOCAL_DRAFT_KEY);
    localStorage.removeItem(LOCAL_DRAFT_ID_KEY);
  } catch {
    /* ignore */
  }
}

export function restoreLocalDraft(state: AnfrageWizardState): AnfrageWizardState {
  try {
    const raw = localStorage.getItem(LOCAL_DRAFT_KEY);
    if (!raw) return state;
    const d = JSON.parse(raw) as Partial<AnfrageWizardState> & {
      recipients?: TaRecipient[];
      snippetTemplateId?: number | null;
    };
    const draftIdRaw = localStorage.getItem(LOCAL_DRAFT_ID_KEY);
    const auftraege = Array.isArray(d.auftraege) ? d.auftraege : [];
    return {
      ...state,
      recipients: Array.isArray(d.recipients) && d.recipients.length ? d.recipients : state.recipients,
      auftraege,
      positionen: Array.isArray(d.positionen) ? d.positionen : [],
      kuerzel: d.kuerzel || state.kuerzel,
      snippetTemplateId:
        d.snippetTemplateId != null && Number.isFinite(Number(d.snippetTemplateId))
          ? Number(d.snippetTemplateId)
          : null,
      draftId: draftIdRaw ? Number(draftIdRaw) : null,
      placeholders: auftraege.length ? 0 : 1,
    };
  } catch {
    return state;
  }
}

export function positionAuftragDisplay(p: AnfragePosition): string {
  return (
    p.auftragsnummer_display ||
    formatAuftragsnummerDisplay({
      id: p.auftrag_id,
      jahr: new Date().getFullYear(),
      auftragsnummer: p.auftragsnummer || "",
    }) ||
    p.auftragsnummer ||
    ""
  );
}

export function uniqueTaNummern(row: { positionen?: AnfragePosition[] | null }): string[] {
  return (
    row.positionen
      ?.map((p) => p.ta_nummer)
      .filter(Boolean)
      .filter((v, i, a) => a.indexOf(v) === i) as string[]
  ) || [];
}

export function plainTextToSafeMailHtml(text: string): string {
  const raw = String(text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  if (!raw.trim()) return '<p style="margin:0;"></p>';
  const esc = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  return raw
    .split("\n\n")
    .map((block) => {
      const inner = block.split("\n").map((line) => esc(line)).join("<br>\n");
      return `<p style="margin:0 0 0.85em 0;">${inner}</p>`;
    })
    .join("\n");
}

export function mailReadDocumentHtml(bodyInnerHtml: string): string {
  const fg = "#1f2937";
  const bg = "#ffffff";
  const muted = "#64748b";
  const font = "'Segoe UI','Calibri','Helvetica Neue',Helvetica,Arial,sans-serif";
  const styles = `html,body{margin:0;padding:0;background:${bg};color:${fg};font-family:${font};font-size:14px;line-height:1.45;word-break:break-word;}
    .eh-mail-html-root img{max-width:100%;height:auto;vertical-align:middle;}
    .eh-mail-html-root table{max-width:100%;}
    .eh-mail-html-root blockquote{border-left:3px solid ${muted};margin:0.5em 0;padding-left:0.75em;color:${muted};}`;
  return `<!DOCTYPE html><html lang="de"><head><meta charset="utf-8"><style>${styles}</style></head><body><div class="eh-mail-html-root" style="margin:12px;">${bodyInnerHtml}</div></body></html>`;
}
